/************************************************************************************
 * [MODULE]: PRODUCT INFORMATION
 * Main components:
 * - l-product-form: Handles form submission and cart interactions
 * - l-variant-selects: Manages variant selection and option changes
 * - l-product-controls: Controls product quantity and variant-specific logic
 * - l-product-quantity-input: Handles quantity input interactions
 *
 * Internal Events:
 * - l:variant:changed: Fired when the variant changes
 * - l:quantity:changed: Fired when the quantity changes
 * - l:product:unavailable: Fired when the product is unavailable
 * - l:variant:option-changed: Fired when the variant option changes
 * - l:sellingPlan:changed: Fired when the selling plan changes
 * - l:addon-products:changed: Fired when the addon products change
 * External Events:
 * - l:cart:closed: Fired when the cart is closed
 *************************************************************************************/
document.addEventListener('l:vendor:loaded', () => {
	if (!customElements.get('l-product-info')) {
		customElements.define(
			'l-product-info',
			class LProductInfo extends HTMLElement {
				quantityInput = undefined
				quantityForm = undefined
				abortController = undefined
				pendingRequestUrl = null
				preProcessHtmlCallbacks = []
				postProcessHtmlCallbacks = []

				constructor() {
					super()
					this._syncQuantityFromHtml = false
					this._preservedSellingPlanId = undefined
					this._onRebuyCartChange = l_debounce(() => {
						this.refreshProductInfoFromCart()
					}, 200)
				}

				connectedCallback() {
					// Elements
					this.productFormEl = this.querySelector('l-product-form')
					this.initializeProductSwapUtility()
					this.selectedSellingPlanId = this.dataset.sellingPlanId
					this.productId = this.dataset.productId
					this.dispatchEvent(new CustomEvent('l:product-info:loaded', { bubbles: true }))
					this.setupEventListeners()
					document.addEventListener('rebuy:cart.change', this._onRebuyCartChange)
				}

				disconnectedCallback() {
					document.removeEventListener('rebuy:cart.change', this._onRebuyCartChange)
				}

				setupEventListeners() {
					// Variant option changes
					document.addEventListener('l:variant:option-changed', (event) => {
						const { target, selectedOptionValues, productId, sectionId } = event.detail
						if (!this.contains(target) || productId !== this.productId || sectionId !== this.sectionId) return
						this.handleOptionValueChange(target, selectedOptionValues, null)
					})

					// Combined product selected
					document.addEventListener('l:combined-product:selected', (event) => {
						const { productLink, targetId } = event.detail

						if (!this.contains(event.target) || productLink === this.dataset.url) return

						const parentElement = this.parentElement
						parentElement.classList.add('is-loading')

						this.renderProductInfo({
							requestUrl: `${productLink}?section_id=${this.sectionId}&view=ajax`,
							targetId: targetId,
							callback: (html) => {
								l_swapHtmlNode(
									this,
									html.querySelector('l-product-info'),
									this.preProcessHtmlCallbacks,
									this.postProcessHtmlCallbacks
								)
							},
							finallyCallback: () => {
								l_debounce(() => {
									parentElement.classList.remove('is-loading')
								}, 500)()
							},
						})
					})

					// Selling plan changes
					document.addEventListener('l:sellingPlan:changed', (event) => {
						const { sellingPlanId, productId, sectionId, target } = event.detail
						if (!this.contains(target) || productId !== this.productId || sectionId !== this.sectionId) return
						const variant = this.getSelectedVariant(this)
						this.selectedSellingPlanId = sellingPlanId
						this.handleOptionValueChange(event.target, null, variant)
					})

					// Product Added: This is used to update the product info when a product is added to the cart
					document.addEventListener('l:product:added', (event) => {
						const response = event.detail?.response
						const currentVariant = this.getSelectedVariant(this)
						const responseProductId = response?.product_id
						const responseVariantId = response?.variant_id || response?.id

						const matchesProduct =
							!responseProductId || String(responseProductId) === String(this.productId)
						const matchesVariant =
							!currentVariant || String(responseVariantId) === String(currentVariant.id)

						if (!matchesProduct) return

						this._preservedSellingPlanId = this.getSelectedSellingPlanId()
						this.selectedSellingPlanId = this._preservedSellingPlanId
						this._syncQuantityFromHtml = true
						this._resetQuantityAfterAdd = true

						if (matchesVariant || currentVariant) {
							this.handleOptionValueChange(null, null, currentVariant)
						}
					})

					// Cart updates
					document.addEventListener('l:cart:closed', (event) => {
						const { updatedVariants } = event.detail
						const variant = this.getSelectedVariant(this)
						const shouldUpdate = updatedVariants.includes(`${variant?.id}`)
						if (shouldUpdate) this.handleOptionValueChange(null, null, variant)
					})
				}

				addPreProcessCallback(callback) {
					this.preProcessHtmlCallbacks.push(callback)
				}

				initializeProductSwapUtility() {
					this.postProcessHtmlCallbacks.push((newNode) => {
						// Re-initialize Shopify functionality after DOM updates
						window?.Shopify?.PaymentButton?.init()
						window?.ProductModel?.loadShopifyXR()
					})
				}

				getSelectedSellingPlanId() {
					const checkedSellingPlan =
						this.querySelector(`#Subscriptions-${this.sectionId} [name="selling_plan"]:checked`) ??
						this.querySelector('[name="selling_plan"]:checked')

					return checkedSellingPlan?.value || null
				}

				refreshProductInfoFromCart() {
					const variant = this.getSelectedVariant(this)
					if (!variant) return

					this._preservedSellingPlanId = this.getSelectedSellingPlanId()
					this.selectedSellingPlanId = this._preservedSellingPlanId
					this._syncQuantityFromHtml = true
					this._resetQuantityAfterAdd = true
					this.handleOptionValueChange(null, null, variant)
				}

				handleOptionValueChange(target, selectedOptionValues, variant) {
					// Reset form state
					this.resetProductFormState()
					const productUrl = target?.dataset?.productUrl || this.pendingRequestUrl || this.dataset.url
					this.pendingRequestUrl = productUrl
					const shouldSwapProduct = this.dataset.url !== productUrl
					const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct
					this.renderProductInfo({
						requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, variant, shouldFetchFullPage),
						targetId: target?.id,
						callback: shouldSwapProduct
							? this.handleSwapProduct(productUrl, shouldFetchFullPage)
							: this.handleUpdateProductInfo(productUrl),
					})
				}

				resetQuantityInputsToMin() {
					this.querySelectorAll('l-product-quantity-input').forEach((qtyComponent) => {
						if (typeof qtyComponent.resetToMinQuantity === 'function') {
							qtyComponent.resetToMinQuantity()
							return
						}

						const input = qtyComponent.querySelector('[data-qty-input]')
						if (!input) return

						const min = parseInt(input.min, 10)
						const nextValue = !isNaN(min) && min > 0 ? min : 1
						const previousValue = parseInt(input.value, 10) || 0

						if (previousValue === nextValue) return

						input.value = nextValue
						input.dispatchEvent(new Event('change', { bubbles: true }))
						qtyComponent.onQuantityChanged?.(previousValue, nextValue)
						qtyComponent.validateQtyRules?.()
					})
				}

				resetProductFormState() {
					if (this.productFormEl) {
						this.productFormEl.global_toggleSubmitButton(true, null, false)
						this.productFormEl.handleErrorMessage()
					}
				}

				renderProductInfo({ requestUrl, targetId, callback, finallyCallback }) {
					// Cancel any pending requests
					this.abortController?.abort()
					this.abortController = new AbortController()

					fetch(requestUrl, { signal: this.abortController.signal })
						.then((response) => response.text())
						.then((responseText) => {
							this.pendingRequestUrl = null
							const html = new DOMParser().parseFromString(responseText, 'text/html')

							callback(html)
						})
						.then(() => {
							if (!targetId) return

							document
								.getElementById(targetId)
								?.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'center' })
						})
						.catch((error) => {
							// Only log actual errors, not aborted requests
							if (error.name !== 'AbortError') {
								console.error('Product info fetch error:', error)
							}
						})
						.finally(() => {
							finallyCallback?.()
						})
				}

				handleSwapProduct(productUrl, updateFullPage) {
					return (html) => {
						const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info'
						const variant = this.getSelectedVariant(html.querySelector(selector))
						// Update URL with new variant ID
						this.updateURL(productUrl, variant?.id)
						if (updateFullPage) {
							// Update page title
							document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML
							// Swap the entire main content
							l_swapHtmlNode(
								document.querySelector('main'),
								html.querySelector('main'),
								this.preProcessHtmlCallbacks,
								this.postProcessHtmlCallbacks
							)
						} else {
							// Swap just the product info section
							l_swapHtmlNode(
								this,
								html.querySelector('product-info'),
								this.preProcessHtmlCallbacks,
								this.postProcessHtmlCallbacks
							)
						}
					}
				}

				handleUpdateProductInfo(productUrl) {
					return (html) => {
						const variant = this.getSelectedVariant(html)
						// Update DOM elements
						this.updateOptionValues(html)
						this.updateURL(productUrl, variant?.id)
						this.updateVariantInputs(variant?.id)
						const resetQuantityAfterAdd = this._resetQuantityAfterAdd
						const syncQuantityFromHtml = this._syncQuantityFromHtml || resetQuantityAfterAdd
						const preservePurchaseOption = this._syncQuantityFromHtml || resetQuantityAfterAdd
						const preservedSellingPlanId = preservePurchaseOption ? this._preservedSellingPlanId : undefined
						const checkedSellingPlan = html
							.getElementById(`Subscriptions-${this.sectionId}`)
							?.querySelector('[name="selling_plan"]:checked')
						this.selectedSellingPlanId = preservePurchaseOption
							? preservedSellingPlanId ?? this.getSelectedSellingPlanId()
							: checkedSellingPlan?.value || null
						this._preservedSellingPlanId = undefined
						this._syncQuantityFromHtml = false
						this._resetQuantityAfterAdd = false
						// Handle unavailable variant
						if (!variant) {
							document.dispatchEvent(
								new CustomEvent('l:variant:changed', {
									detail: {
										sectionId: this.sectionId,
										html,
										productId: this.dataset.productId,
										variant,
										syncQuantityFromHtml,
										resetQuantityAfterAdd,
										preservePurchaseOption,
										preservedSellingPlanId,
									},
								})
							)

							if (resetQuantityAfterAdd) {
								this.resetQuantityInputsToMin()
							}
							return
						}
						// Update product sections from HTML response
						this.updateProductSections(html)
						// Notify about variant change (quantity rules apply from fetched HTML here)
						document.dispatchEvent(
							new CustomEvent('l:variant:changed', {
								detail: {
									sectionId: this.sectionId,
									html,
									productId: this.dataset.productId,
									variant,
									syncQuantityFromHtml,
									resetQuantityAfterAdd,
									preservePurchaseOption,
									preservedSellingPlanId,
								},
							})
						)

						// Reset qty after new HTML rules have been applied
						if (resetQuantityAfterAdd) {
							this.resetQuantityInputsToMin()
						}
					}
				}

				updateProductSections(html) {
					const updateSourceFromDestination = (id, htmlMethod = 'innerHTML', shouldHide = () => false) => {
						const source = html.getElementById(`${id}-${this.sectionId}`)
						const destination = document.getElementById(`${id}-${this.dataset.section}`)
						if (source && destination) {
							destination[htmlMethod] = source[htmlMethod]
							destination.classList.toggle('l-hidden', shouldHide(source))
						}
					}
					// Update product sections
					updateSourceFromDestination('stockInfo', 'outerHTML')
					updateSourceFromDestination('stickyATC', 'outerHTML')
				}

				updateOptionValues(html) {
					const variantSelects = html.querySelector('l-variant-selects')
					if (variantSelects) {
						l_swapHtmlNode(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks)
					}
				}

				updateURL(url, variantId) {
					if (this.dataset.updateUrl === 'false') return

					const currentUrlParams = new URLSearchParams(window.location.search)
					const params = new URLSearchParams()

					if (variantId) params.append('variant', variantId)

					if (this.selectedSellingPlanId) params.append('selling_plan', this.selectedSellingPlanId)

					const viewTemplate = currentUrlParams.get('view')
					if (viewTemplate) params.append('view', viewTemplate)

					const newUrl = `${url}${params.size > 0 ? '?' : ''}${params.toString()}`

					window.history.replaceState({}, '', newUrl)
				}

				updateVariantInputs(variantId) {
					this.querySelectorAll(`#product-form-${this.dataset.section}`).forEach((productForm) => {
						const input = productForm.querySelector('input[name="id"]')
						input.value = variantId ?? ''
						input.dispatchEvent(new Event('change', { bubbles: true }))
					})
				}

				getSelectedVariant(productInfoNode) {
					const selectedVariant = productInfoNode.querySelector(
						'l-variant-selects [data-selected-variant], [data-selected-variant]'
					)?.innerHTML

					return selectedVariant ? JSON.parse(selectedVariant) : null
				}

				buildRequestUrlWithParams(url, optionValues, variant, shouldFetchFullPage = false) {
					const params = []
					// Add section ID if not fetching full page
					if (!shouldFetchFullPage) {
						params.push(`section_id=${this.sectionId}`)
					}
					// Add selling plan if selected
					if (this.selectedSellingPlanId) {
						params.push(`selling_plan=${this.selectedSellingPlanId}`)
					}
					// Add option values or variant ID
					if (optionValues?.length) {
						params.push(`option_values=${optionValues.join(',')}`)
					} else if (variant) {
						params.push(`variant=${variant.id}`)
					}
					return `${url}?${params.join('&')}`
				}

				get productForm() {
					return this.querySelector('l-product-form')
				}

				get variantSelectors() {
					return this.querySelector('l-variant-selects')
				}

				get sectionId() {
					return this.dataset.originalSection || this.dataset.section
				}
			}
		)
	}

	if (!customElements.get('l-product-form')) {
		customElements.define(
			'l-product-form',
			class LTAProductForm extends HTMLElement {
				constructor() {
					super()
					this._boundHandleOnQuantityBreaksChanged = this.handleOnQuantityBreaksChanged.bind(this)
				}

				connectedCallback() {
					// Cache DOM elements
					this.form = this.querySelector('form') || document.getElementById(this.dataset.formId)
					this.cart = document.getElementById('CartDrawer')
					this.submitButtonEl = this.querySelector('[type="submit"]')
					this.submitButtonTextEl = this.submitButtonEl.querySelector('[data-add-to-cart-label]')
					this.dynamicCheckoutEl = this.querySelector('[data-dynamic-checkout]')
					this.backInStockButtonEl = this.querySelector('[data-open-back-in-stock-trigger]')

					// Store section data
					this.sectionId = this.dataset.sectionId
					this.productId = this.dataset.productId
					this.selectedVariantId = this.dataset.selectedVariantId
					this.hideErrors = this.dataset.hideErrors === 'true'
					this.data = {
						pricePerUnit: this.dataset.defaultPricePerUnit,
						quantity: null,
						savingsPercentage: null,
					}

					// Initialize event listeners
					if (this.form) {
						this.form.addEventListener('submit', this.onSubmitHandler.bind(this))
					}

					document.addEventListener('l:variant:changed', this.handleOnVariantChange.bind(this))
					document.addEventListener('l:quantity:changed', this._boundHandleOnQuantityBreaksChanged)
				}

				disconnectedCallback() {
					document.removeEventListener('l:variant:changed', this.handleOnVariantChange)
					document.removeEventListener('l:quantity:changed', this._boundHandleOnQuantityBreaksChanged)
				}

				handleOnQuantityBreaksChanged(event) {
					const { sectionId, productId, savingsPercentage, quantity } = event.detail || {}

					if (productId !== this.productId || sectionId !== this.sectionId) return

					this.data.quantity = quantity
					if (savingsPercentage) {
						this.data.savingsPercentage = savingsPercentage
					}

					const newPriceEl = this.querySelector('[data-product-normal-price]')
					if (newPriceEl) {
						newPriceEl.textContent = l_formatMoney(this.data.pricePerUnit * (this.data.quantity ?? 1))
					}
				}

				handleOnVariantChange(event) {
					const { sectionId, html, productId, variant } = event.detail

					if (productId !== this.productId || sectionId !== this.sectionId) return

					const newPricePerUnit = html.querySelector('l-product-form')?.dataset?.defaultPricePerUnit
					if (newPricePerUnit) {
						this.data.pricePerUnit = newPricePerUnit
					}

					// Update back in stock button
					if (this.backInStockButtonEl) {
						this.backInStockButtonEl.classList.toggle('l-hidden', !variant || (variant && variant.available))
					}

					// Hide items if variant is not available
					if (this.dynamicCheckoutEl) {
						this.dynamicCheckoutEl.classList.toggle('l-hidden', !variant)
					}

					// Hide submit button if variant is not exists
					if (!variant) {
						this.global_toggleSubmitButton(true, 'unavailable')

						return
					}

					// Replace submit button
					const newSubmitButtonEl = html.getElementById(`ProductSubmitButton-${this.sectionId}`)

					if (!newSubmitButtonEl) return
					const newPriceEl = newSubmitButtonEl.querySelector('[data-product-normal-price]')
					if (newPriceEl) {
						newPriceEl.textContent = l_formatMoney(this.data.pricePerUnit * (this.data.quantity ?? 1))
					}

					this.submitButtonEl.replaceWith(newSubmitButtonEl)

					// Re-query elements
					this.submitButtonEl = this.querySelector('[type="submit"]')
					this.submitButtonTextEl = this.submitButtonEl.querySelector('[data-add-to-cart-label]')
				}

				onSubmitHandler(evt) {
					evt.preventDefault()

					// Don't submit if button is disabled
					if (this.submitButtonEl.getAttribute('aria-disabled') === 'true') return

					// Set button to loading state
					this.submitButtonEl.setAttribute('aria-disabled', true)
					this.submitButtonEl.classList.add('is-loading')

					// Create cart configuration and add to cart
					const config = ltaCartService.createConfigFromForm(this.form)
					const productInfo = this.closest('l-product-info')

					// Mark product-info so quantity resets after the post-add HTML refresh
					if (productInfo) {
						productInfo._preservedSellingPlanId = productInfo.getSelectedSellingPlanId?.()
						productInfo.selectedSellingPlanId = productInfo._preservedSellingPlanId
						productInfo._syncQuantityFromHtml = true
						productInfo._resetQuantityAfterAdd = true
					}

					ltaCartService
						.addToCart(config, 'l-product-form')
						.then((response) => {
							if (response?.status) {
								if (productInfo) productInfo._resetQuantityAfterAdd = false
								this.handleErrorMessage(response.description)
							}
						})
						.catch((e) => {
							if (productInfo) productInfo._resetQuantityAfterAdd = false
							console.error('Add to cart error:', e)
						})
						.finally(() => {
							// Remove loading state
							this.submitButtonEl.classList.remove('is-loading')

							if (!this.error) this.submitButtonEl.removeAttribute('aria-disabled')

							l_debounce(() => this.handleErrorMessage.bind(this, false), 1000)()
						})
				}

				handleErrorMessage(errorMessage = false) {
					if (this.hideErrors) return

					// Find error message elements if not already cached
					if (!this.errorMessageWrapper) {
						this.errorMessageWrapper = document.getElementById(`ProductErrorMessages-${this.sectionId}`)
						this.errorMessage = this.errorMessageWrapper?.querySelector('[data-error-message]')
					}

					if (!this.errorMessageWrapper) return

					// Toggle error message visibility
					this.errorMessageWrapper.classList.toggle('l-hidden', !errorMessage)
					if (errorMessage) this.errorMessage.textContent = errorMessage
				}

				global_toggleSubmitButton(disable = true, textType, needToUpdateText = true) {
					if (disable) {
						this.submitButtonEl.setAttribute('disabled', 'disabled')
						if (textType && needToUpdateText) this.submitButtonTextEl.textContent = this.getButtonLabel(textType)

						return
					}

					this.submitButtonEl.removeAttribute('disabled')

					if (needToUpdateText) {
						this.submitButtonTextEl.textContent = this.getButtonLabel(textType)
					}
				}

				getButtonLabel(textType = 'addToCart') {
					switch (textType) {
						case 'addToCart':
							return this.dataset.atcLabel || window.LTATheme.variantStrings.addToCart
						case 'soldOut':
							return this.dataset.soldOutLabel || window.LTATheme.variantStrings.soldOut
						case 'preOrder':
							return this.dataset.preOrderLabel || window.LTATheme.variantStrings.preOrder
						case 'unavailable':
							return this.dataset.unavailableLabel || window.LTATheme.variantStrings.unavailable
						default:
							return window.LTATheme.variantStrings.addToCart
					}
				}

				get variantIdInput() {
					return this.form.querySelector('[name=id]')
				}
			}
		)
	}

	if (!customElements.get('l-variant-selects')) {
		class LTAVariantSelect extends HTMLElement {
			constructor() {
				super()
				this.onChange = this.onChange.bind(this)
			}

			connectedCallback() {
				this.productId = this.dataset.productId
				this.sectionId = this.dataset.sectionId
				this.addEventListener('change', this.onChange)
			}

			onChange(event) {
				const target = this._getInputFromEvent(event.target)
				this._updateSelectionMetadata(target)

				document.dispatchEvent(
					new CustomEvent('l:variant:option-changed', {
						detail: {
							event,
							target,
							selectedOptionValues: this.selectedOptionValues,
							productId: this.productId,
							sectionId: this.sectionId,
						},
					})
				)
			}

			_updateSelectionMetadata(target) {
				const { value, tagName } = target

				// Skip for select elements with selected options
				if (tagName === 'SELECT' && target.selectedOptions.length) return

				// Update selected value display for swatch inputs
				if (target.hasAttribute('data-product-variant-input')) {
					const optionsContainer = target.closest('[data-variant-options]')
					if (!optionsContainer) return
					const selectedSwatchValue = optionsContainer.querySelector('[data-selected-value]')
					if (selectedSwatchValue) {
						selectedSwatchValue.innerHTML = value
					}
				}
			}

			_getInputFromEvent(target) {
				return target.tagName === 'SELECT' ? target.selectedOptions[0] : target
			}

			get selectedOptionValues() {
				return Array.from(
					this.querySelectorAll('[data-variant-select] option[selected], [data-product-variant-input]:checked')
				).map((el) => el.dataset.optionValueId)
			}
		}

		customElements.define('l-variant-selects', LTAVariantSelect)
	}

	if (!customElements.get('l-product-quantity-input')) {
		class LTAProductQuantityInput extends customElements.get('l-qty-input') {
			constructor() {
				super()
				this.onVariantChanged = this.updateQuantityRules.bind(this)
			}

			connectedCallback() {
				super.connectedCallback()

				if (!this.input) return

				this.productId = this.dataset.productId
				this.sectionId = this.dataset.sectionId

				document.addEventListener('l:variant:changed', this.onVariantChanged)
			}

			onQuantityChanged(oldValue, newValue) {
				this.input.dispatchEvent(
					new CustomEvent('l:quantity:changed', {
						bubbles: true,
						detail: {
							quantity: newValue,
							productId: this.productId,
							sectionId: this.sectionId,
						},
					})
				)
			}

			resetToMinQuantity() {
				if (!this.input) return

				const min = parseInt(this.input.min, 10)
				const nextValue = !isNaN(min) && min > 0 ? min : 1
				const previousValue = parseInt(this.input.value, 10) || 0

				if (previousValue === nextValue) {
					this.validateQtyRules()
					return
				}

				this.input.value = nextValue

				if (this.changeEvent) {
					this.input.dispatchEvent(this.changeEvent)
				} else {
					this.input.dispatchEvent(new Event('change', { bubbles: true }))
				}

				this.onQuantityChanged(previousValue, nextValue)
				this.validateQtyRules()
			}

			updateQuantityRules(event) {
				const { html, sectionId, productId, variant, syncQuantityFromHtml, resetQuantityAfterAdd } = event.detail

				if (String(productId) !== String(this.productId) || String(sectionId) !== String(this.sectionId) || !variant) {
					return
				}

				const blockId = this.dataset.blockId
				let newInputEl

				if (blockId) {
					newInputEl = html.getElementById(`Product-Quantity-Selector-${blockId}`)?.querySelector('[data-qty-input]')
				} else {
					const quantityFormUpdated = html.getElementById(`Product-Form-Controls-${sectionId}`)
					newInputEl = quantityFormUpdated?.querySelector('[data-qty-input]')
				}

				if (!newInputEl) {
					if (resetQuantityAfterAdd || syncQuantityFromHtml) {
						this.resetToMinQuantity()
					}
					return
				}

				const previousVariantId = parseInt(this.dataset.selectedVariantId, 10)
				const isSameVariant = previousVariantId === variant.id
				const previousValue = parseInt(this.input.value, 10)
				const shouldResetQuantity = Boolean(resetQuantityAfterAdd || syncQuantityFromHtml)

				this.input.setAttribute('min', newInputEl.min)
				this.input.setAttribute('max', newInputEl.max)
				this.input.setAttribute('step', newInputEl.step)
				this.input.toggleAttribute('disabled', newInputEl.disabled)

				this.querySelectorAll('[data-qty-btn]').forEach((button, index) => {
					const newButton = newInputEl.closest('l-product-quantity-input')?.querySelectorAll('[data-qty-btn]')[index]
					if (newButton) {
						button.toggleAttribute('disabled', newButton.disabled)
					}
				})

				if (shouldResetQuantity) {
					const min = parseInt(newInputEl.min, 10) || 1
					this.input.value = min
				} else if (!isSameVariant || isNaN(previousValue)) {
					this.input.value = newInputEl.value
				} else {
					const min = parseInt(newInputEl.min, 10) || 1
					const max = parseInt(newInputEl.max, 10)
					let nextValue = Math.max(previousValue, min)
					if (!isNaN(max) && max > 0) nextValue = Math.min(nextValue, max)
					this.input.value = nextValue
				}

				this.dataset.selectedVariantId = variant.id

				if (shouldResetQuantity || !isSameVariant || parseInt(this.input.value, 10) !== previousValue) {
					this.input.dispatchEvent(this.changeEvent)
					this.onQuantityChanged(previousValue, parseInt(this.input.value, 10))
				}

				this.validateQtyRules() // Validate quantity rules
			}

			disconnectedCallback() {
				document.removeEventListener('l:variant:changed', this.onVariantChanged)
				super.disconnectedCallback()
			}
		}

		customElements.define('l-product-quantity-input', LTAProductQuantityInput)
	}

	// Note: placed this component at the end of the file to avoid race conditions
	if (!customElements.get('l-product-price')) {
		class LTAProductPrice extends HTMLElement {
			constructor() {
				super()
				this.addOnPrice = {}

				this.handleOnQuantityChange = this.handleOnQuantityChange.bind(this)
				this.handleOnVariantChange = this.handleOnVariantChange.bind(this)
				this.handleOnAddOnChange = this.handleOnAddOnChange.bind(this)
			}

			connectedCallback() {
				this.sectionId = this.dataset.sectionId
				this.productId = this.dataset.productId

				this._cacheElements()
				this._updateJsonData(this)

				document.addEventListener('l:quantity:changed', this.handleOnQuantityChange)
				document.addEventListener('l:variant:changed', this.handleOnVariantChange)
				document.addEventListener('l:addon-products:changed', this.handleOnAddOnChange)
			}

			disconnectedCallback() {
				document.removeEventListener('l:quantity:changed', this.handleOnQuantityChange)
				document.removeEventListener('l:variant:changed', this.handleOnVariantChange)
				document.removeEventListener('l:addon-products:changed', this.handleOnAddOnChange)
			}

			_cacheElements() {
				this.priceEl = this.querySelector('[data-price]')
				this.compareAtPriceEl = this.querySelector('[data-compare-at-price]')
				this.saleTagEl = this.querySelector('[data-sale-tag]')
			}

			handleOnQuantityChange(event) {
				const { sectionId, productId, quantity } = event.detail

				if (productId !== this.productId || sectionId !== this.sectionId) return

				this.priceData = {
					...this.priceData,
					quantity,
				}

				this._updatePrice()
			}

			handleOnVariantChange(event) {
				const { sectionId, html, productId, variant } = event.detail
				if (productId !== this.productId || sectionId !== this.sectionId) return

				this.classList.toggle('l-hidden', !variant)
				if (!variant) return

				const newPriceEl = html.getElementById(`price-${sectionId}`)
				if (newPriceEl) this._updateJsonData(newPriceEl)

				this._updatePrice()
			}

			handleOnAddOnChange(event) {
				const { id, totalNormalPrice, totalComparePrice, productId, sectionId } = event.detail
				if (productId !== this.productId || sectionId !== this.sectionId) return

				this.addOnPrice[id] = { totalNormalPrice, totalComparePrice }
				this._updatePrice()
			}

			_updateJsonData(priceEl) {
				const script = priceEl.querySelector('script[type="application/json"]')
				if (!script) {
					this.priceData = {}
					return
				}

				let newPriceData = JSON.parse(script.textContent)

				const minQty = Number(newPriceData.min ?? 1)
				newPriceData = {
					...newPriceData,
					quantity: minQty,
				}

				this.priceData = newPriceData
			}

			_updatePrice() {
				const {
					comparePrice,
					normalPrice,
					newNormalPriceFormatted,
					newComparePriceFormatted,
					salePercentage,
					salePriceFormatted,
					saleTagLabel,
				} = this._calculatePrices()

				if (this.priceEl) this.priceEl.innerHTML = newNormalPriceFormatted

				const hideCompare = !comparePrice || comparePrice === 0 || comparePrice <= normalPrice
				if (this.compareAtPriceEl) {
					this.compareAtPriceEl.classList.toggle('l-hidden', hideCompare)
					if (!hideCompare) {
						this.compareAtPriceEl.innerHTML = newComparePriceFormatted
					}
				}

				if (this.saleTagEl && saleTagLabel) {
					this.saleTagEl.classList.toggle('l-hidden', !comparePrice || comparePrice <= normalPrice)

					this.saleTagEl.innerHTML = saleTagLabel
						.replace('[percentage]', `${salePercentage}%`)
						.replace('[amount]', salePriceFormatted)
				}

				const globalNormalPriceEls = document.querySelectorAll(`[data-product-normal-price="${this.sectionId}"]`)
				globalNormalPriceEls.forEach((el) => {
					el.innerHTML = newNormalPriceFormatted
				})
			}

			_calculatePrices() {
				let {
					normalPrice = 0,
					comparePrice = 0,
					saleTagLabel = '',
					quantity = 1,
					quantity_price_breaks = [],
				} = this.priceData

				if (Array.isArray(quantity_price_breaks) && quantity_price_breaks.length > 0) {
					const priceBreak = quantity_price_breaks
						.slice()
						.sort((a, b) => a.minimumQuantity - b.minimumQuantity)
						.reduce((acc, curr) => (quantity >= curr.minimumQuantity ? curr : acc), null)

					if (priceBreak) normalPrice = priceBreak.money
				}

				const totalAddOnNormalPrice = this._getTotalAddOn('totalNormalPrice')
				const totalAddOnComparePrice = this._getTotalAddOn('totalComparePrice')

				const newNormalPrice = normalPrice * quantity + totalAddOnNormalPrice
				const newComparePrice = comparePrice * quantity + totalAddOnComparePrice

				const newNormalPriceFormatted = l_formatMoney(newNormalPrice)
				const newComparePriceFormatted = l_formatMoney(newComparePrice)

				const salePercentage =
					newComparePrice > 0 ? parseFloat(((newComparePrice - newNormalPrice) / newComparePrice) * 100).toFixed(0) : 0
				const salePriceFormatted = l_formatMoney(newComparePrice - newNormalPrice)

				return {
					normalPrice,
					comparePrice,
					salePercentage,
					newNormalPriceFormatted,
					newComparePriceFormatted,
					salePriceFormatted,
					saleTagLabel,
				}
			}

			_getTotalAddOn(key) {
				return Object.values(this.addOnPrice).reduce((acc, curr) => acc + (curr[key] || 0), 0)
			}
		}

		customElements.define('l-product-price', LTAProductPrice)
	}

	if (!customElements.get('l-product-installment-payments')) {
		class LTAProductInstallmentPayments extends HTMLElement {
			constructor() {
				super()
				this._boundOnVariantChange = this._onVariantChange.bind(this)
			}

			connectedCallback() {
				this.sectionId = this.dataset.sectionId
				this.productId = this.dataset.productId

				this._applyInstallmentsFont()

				document.addEventListener('l:variant:changed', this._boundOnVariantChange)
			}

			disconnectedCallback() {
				document.removeEventListener('l:variant:changed', this._boundOnVariantChange)
				this._installmentsObserver?.disconnect()
			}

			_applyInstallmentsFont() {
				const inject = (el, retries = 10) => {
					if (!el.shadowRoot) {
						if (retries > 0) requestAnimationFrame(() => inject(el, retries - 1))
						return
					}
					if (el.shadowRoot.getElementById('l-installments-font')) return
					const style = document.createElement('style')
					style.id = 'l-installments-font'
					style.textContent = `* { font-family: inherit !important; }`
					el.shadowRoot.appendChild(style)
				}

				const el = this.querySelector('shopify-payment-terms')
				if (el) return inject(el)

				this._installmentsObserver = new MutationObserver(() => {
					const target = this.querySelector('shopify-payment-terms')

					if (target) {
						inject(target)
						this._installmentsObserver.disconnect()
					}
				})
				this._installmentsObserver.observe(this, { childList: true, subtree: true })
			}

			_onVariantChange(event) {
				const { sectionId, productId, variant } = event.detail || {}
				if (productId !== this.productId || sectionId !== this.sectionId || !variant) return

				const inputs = this.querySelectorAll('input[name="id"]')
				inputs.forEach((input) => {
					input.value = variant.id
				})
			}
		}

		customElements.define('l-product-installment-payments', LTAProductInstallmentPayments)
	}

	/************************************************************************************
	 *
	 * [MODULE]: PRODUCT MEDIA
	 *
	 *************************************************************************************/

	if (!customElements.get('l-product-media-gallery')) {
		class ProductMediaGallery extends HTMLElement {
			constructor() {
				super()
				this.sectionId = this.dataset.sectionId
				this.productId = this.dataset.productId

				this._boundOnVariantChange = this.onVariantChange.bind(this)
			}

			connectedCallback() {
				this.productId = this.dataset.productId
				this.sectionId = this.dataset.sectionId

				this._initModalZoomEvents()
				this.setupCarousel()

				document.addEventListener('l:variant:changed', this._boundOnVariantChange)
			}

			disconnectedCallback() {
				document.removeEventListener('l:variant:changed', this._boundOnVariantChange)
			}

			_initModalZoomEvents() {
				const zoomMediaBtnEls = this.querySelectorAll('[data-modal-zoom-media]')
				zoomMediaBtnEls.forEach((btn) => {
					btn.addEventListener('click', () => {
						this.dispatchEvent(
							new CustomEvent('l:media:click', {
								bubbles: true,
								detail: { mediaId: btn.dataset.modalZoomMedia },
							})
						)
					})
				})
			}

			setupCarousel() {
				this._setupThumbnailCarousel()
				this._setupMainCarousel()
				this._syncCarousels()
				this._bindSlideChangeEvent()
			}

			_setupThumbnailCarousel() {
				this.thumbnailCarousel = this.querySelector('[data-thumbnails-carousel]')
				this.thumbnailSlides = this.thumbnailCarousel.querySelectorAll('[data-media]')
				this.thumbnailSlides.forEach((slide) => slide.classList.remove('l-hidden'))

				this.thumbnailCarouselSwiper = new window.Swiper(this.thumbnailCarousel, {
					loop: false,
					slidesPerView: 'auto',
					spaceBetween: 8,
					resistanceRatio: 0.2,
					navigation: {
						nextEl: this.thumbnailCarousel.querySelector('[data-next]'),
						prevEl: this.thumbnailCarousel.querySelector('[data-prev]'),
					},
				})
			}

			_setupMainCarousel() {
				this.mainCarouselEl = this.querySelector('[data-main-carousel]')
				this.mediaSlidesEls = this.mainCarouselEl.querySelectorAll('[data-media]')
				this.navigationNextEl = this.mainCarouselEl.querySelector('[data-next]')
				this.navigationPrevEl = this.mainCarouselEl.querySelector('[data-prev]')

				this.mediaSlidesEls.forEach((slide) => slide.classList.remove('l-hidden'))

				this.mainCarouselSwiper = new window.Swiper(this.mainCarouselEl, {
					loop: false,
					slidesPerView: 'auto',
					spaceBetween: 0,
					autoHeight: true,
					keyboard: { enabled: true },
					navigation: {
						nextEl: this.navigationNextEl,
						prevEl: this.navigationPrevEl,
					},
					thumbs: {
						swiper: this.thumbnailCarouselSwiper,
					},
				})
			}

			_syncCarousels() {
				if (this.mainCarouselSwiper && this.thumbnailCarouselSwiper) {
					this.mainCarouselSwiper.controller.control = this.thumbnailCarouselSwiper
				}
			}

			_bindSlideChangeEvent() {
				if (!this.mainCarouselSwiper) return

				this.mainCarouselSwiper.on('slideChange', (swiper) => {
					const activeSlideEl = this.mediaSlidesEls[swiper.activeIndex]

					l_pauseAllMedia()
					l_playMedia(activeSlideEl)
				})
			}

			onVariantChange(event) {
				const { sectionId, productId, variant } = event.detail || {}
				if (productId !== this.productId || sectionId !== this.sectionId || !variant) return

				const featuredMediaId = variant?.featured_media?.id
				let activeSlideIndex = 0
				if (featuredMediaId) {
					activeSlideIndex = Array.from(this.mediaSlidesEls).findIndex(
						(mediaSlideEl) => mediaSlideEl.dataset.mediaId == `${featuredMediaId}`
					)
					if (activeSlideIndex === -1) activeSlideIndex = 0
				}
				this.mainCarouselSwiper.slideTo(activeSlideIndex, true)
			}
		}

		customElements.define('l-product-media-gallery', ProductMediaGallery)
	}

	/*****************************************
	 * [COMPONENT]: EXTERNAL MEDIA
	 ******************************************/

	class LTAProductModalExternalMedia extends HTMLElement {
		constructor() {
			super()

			this.templateEl = null
			this.templateContentEl = null
		}

		connectedCallback() {
			this.templateEl = this.querySelector('template')
		}

		playVideo() {
			if (!this.templateContentEl && this.templateEl) {
				this.templateContentEl = this.templateEl.content.cloneNode(true)
			}

			if (this.templateContentEl) {
				this.appendChild(this.templateContentEl)
			}
		}

		pauseVideo() {
			// this.innerHTML = ''
			// this.templateContentEl = null // Reset the content so video can be played again
			// l_pauseAllMedia(this)
		}
	}

	customElements.define('l-external-media', LTAProductModalExternalMedia)

	/************************************************************************************
	 *
	 * [SECTIONS]: PRODUCT ADDITIONAL COMPONENTS
	 *
	 *************************************************************************************/

	/**********************************
	 * [PRODUCT BLOCK]: SUBSCRIPTIONS
	 ***********************************/

	if (!customElements.get('l-subscriptions-selling-plans-v2')) {
		class LTASubscriptionSellingPlansV2 extends HTMLElement {
			constructor() {
				super()
				this._onVariantChange = this.handleOnVariantChange.bind(this)
				this._onQuantityChange = this.handleOnQuantityChange.bind(this)
				this._onChange = this.handleOnChange.bind(this)
				this._onSellingPlanSelect = this.handleSellingPlanSelect.bind(this)
				this._onMilestoneClick = this.handleMilestoneClick.bind(this)
				this._onCartUpdate = l_debounce(this.handleOnCartUpdate.bind(this), 200)
			}

			connectedCallback() {
				this.init()
				this.disableAppInputsIfNeeded()

				document.addEventListener('l:variant:changed', this._onVariantChange)
				document.addEventListener('l:quantity:changed', this._onQuantityChange)
				document.addEventListener('l:product:added', this._onCartUpdate)
				document.addEventListener('rebuy:cart.change', this._onCartUpdate)
				this.addEventListener('change', this._onChange)
				this.addEventListener('click', this._onMilestoneClick)
			}

			disconnectedCallback() {
				document.removeEventListener('l:variant:changed', this._onVariantChange)
				document.removeEventListener('l:quantity:changed', this._onQuantityChange)
				document.removeEventListener('l:product:added', this._onCartUpdate)
				document.removeEventListener('rebuy:cart.change', this._onCartUpdate)
				this.removeEventListener('change', this._onChange)
				this.removeEventListener('click', this._onMilestoneClick)
				this.sellingPlanSelectEl?.removeEventListener('change', this._onSellingPlanSelect)
			}

			init() {
				this.saveLabelEl = this.querySelector('[data-save-label]')
				this.variantPriceEl = this.querySelector('[data-variant-price]')
				this.sellingPriceEl = this.querySelector('[data-selling-price]')
				this.sellingComparePriceEl = this.querySelector('[data-selling-compare-price]')
				this.priceEls = this.querySelectorAll('[data-price]')
				this.sellingPlanSelectEl = this.querySelector('[data-selling-plan-select]')
				this.qtyBreaksEl = this.querySelector('[data-qty-breaks]')
				this.qtyBreaksFillEl = this.querySelector('[data-qty-breaks-fill]')
				this.qtyBreaksMessageEl = this.querySelector('[data-qty-breaks-message]')
				this.qtyBreakMilestoneEls = this.querySelectorAll('[data-qty-breaks-milestone]')
				this.subscriptionBodyEl = this.querySelector('[data-subscription-body]')
				this.maxTierPanelEl = this.querySelector('[data-max-tier-panel]')
				this.maxTierBoxEl = this.querySelector('[data-max-tier-box]')
				this.maxTierBoxContentEl = this.querySelector('[data-max-tier-box-content]')

				this.productId = this.dataset.productId
				this.sectionId = this.dataset.sectionId

				const script = this.querySelector('script[type="application/json"]')
				this.subscriptionData = {
					...(script ? JSON.parse(script.textContent) : {}),
					showQuantityBreaks: false,
					maxTierBoxAvailable: true,
				}

				this.tiers = []
				this.selectedVariantId = this.subscriptionData?.id
				this.selectedSellingPlanId = this.querySelector('[name="selling_plan"]:checked')?.value
				this.cartSubscriptionQty = parseInt(this.subscriptionData.cartSubscriptionQty, 10) || 0
				this.cartSubscriptionLineCount = parseInt(this.subscriptionData.cartSubscriptionLineCount, 10) || 0

				const inputQuantity = this.getCurrentQuantity()
				this.subscriptionData.quantity = inputQuantity

				this.logQtyBreaksState('init')

				this.hideQtyBreaks()
				this.syncSellingPlanSelect()
				this.syncPurchasePanels()
				this.initQtyBreakTiers(this.getEffectiveQuantity())
				this.updatePrices(inputQuantity)

				this.sellingPlanSelectEl?.removeEventListener('change', this._onSellingPlanSelect)
				this.sellingPlanSelectEl?.addEventListener('change', this._onSellingPlanSelect)
			}

			hideQtyBreaks() {
				this.subscriptionData.showQuantityBreaks = false
				this.qtyBreaksEl?.classList.add('l-hidden')
				this.qtyBreaksEl?.setAttribute('hidden', '')
			}

			hideMaxTierBox() {
				this.subscriptionData.maxTierBoxAvailable = false
				this.maxTierPanelEl?.classList.add('l-hidden')
				this.maxTierPanelEl?.setAttribute('hidden', '')
				this.maxTierPanelEl?.setAttribute('aria-hidden', 'true')
			}

			updateMaxTierBoxContent(savings) {
				if (!this.maxTierBoxContentEl || !this.subscriptionData.maxTierBoxMessage) return

				const resolvedSavings =
					savings ?? this.tiers[this.tiers.length - 1]?.savings ?? this.subscriptionData.maxTierDefaultSavings ?? ''

				this.maxTierBoxContentEl.innerHTML = this.formatMaxTierMessage(
					this.subscriptionData.maxTierBoxMessage,
					resolvedSavings
				)
			}

			initQtyBreakTiers(currentQuantity) {
				const isEligible = this.dataset.qtyBreaksEligible === 'true'
				const tiers = Array.isArray(this.subscriptionData.tiers) ? this.subscriptionData.tiers : []

				if (!isEligible || !tiers.length) {
					this.hideQtyBreaks()
					this.hideMaxTierBox()
					return
				}

				this.tiers = tiers
				this.subscriptionData.maxTierBoxAvailable = true
				this.qtyBreakMilestoneEls = this.querySelectorAll('[data-qty-breaks-milestone]')
				this.positionMilestones()
				this.updateMaxTierBoxContent()

				if (this.isSubscriptionSelected()) {
					this.showQtyBreaks()
				} else {
					this.hideQtyBreaks()
				}

				this.updateQtyBreaks(currentQuantity)
				this.syncPurchasePanels()
				this.logQtyBreaksState('initQtyBreakTiers')
			}

			showQtyBreaks() {
				this.subscriptionData.showQuantityBreaks = true
				this.qtyBreaksEl?.classList.remove('l-hidden')
				this.qtyBreaksEl?.removeAttribute('hidden')
			}

			getCurrentQuantity() {
				const qtyInput = document.querySelector(
					`l-product-quantity-input[data-section-id="${this.sectionId}"] [data-qty-input]`
				)
				return parseInt(qtyInput?.value, 10) || 1
			}

			getCartSubscriptionQty() {
				return parseInt(this.cartSubscriptionQty, 10) || 0
			}

			getEffectiveQuantity() {
				return this.getCartSubscriptionQty() + this.getCurrentQuantity()
			}

			buildProductSectionRequestUrl() {
				const productInfo = this.closest('l-product-info')
				const productUrl = productInfo?.dataset?.url || window.location.pathname
				const params = [`section_id=${this.sectionId}`]

				if (this.selectedSellingPlanId) {
					params.push(`selling_plan=${this.selectedSellingPlanId}`)
				}

				const variantId = this.selectedVariantId || this.subscriptionData?.id
				if (variantId) {
					params.push(`variant=${variantId}`)
				}

				return `${productUrl}?${params.join('&')}`
			}

			async refreshCartSubscriptionQty() {
				if (this.dataset.qtyBreaksEligible !== 'true') return

				try {
					const response = await fetch(this.buildProductSectionRequestUrl())
					if (!response.ok) throw new Error(`Section fetch responded with ${response.status}`)

					const html = new DOMParser().parseFromString(await response.text(), 'text/html')
					const script = html
						.getElementById(`Subscriptions-${this.sectionId}`)
						?.querySelector('script[type="application/json"]')

					if (!script) return

					const data = JSON.parse(script.textContent)
					this.cartSubscriptionQty = parseInt(data.cartSubscriptionQty, 10) || 0
					this.cartSubscriptionLineCount = parseInt(data.cartSubscriptionLineCount, 10) || 0
					this.subscriptionData = {
						...this.subscriptionData,
						cartSubscriptionQty: this.cartSubscriptionQty,
						cartSubscriptionLineCount: this.cartSubscriptionLineCount,
					}

					if (this.subscriptionData.showQuantityBreaks) {
						this.updateQtyBreaks(this.getEffectiveQuantity())
					}

					this.updatePrices(this.getCurrentQuantity())
					this.logQtyBreaksState('refreshCartSubscriptionQty')
				} catch (error) {
					console.error(error)
				}
			}

			handleOnCartUpdate() {
				this.refreshCartSubscriptionQty()
			}

			logQtyBreaksState(context = '', passedQuantity = null) {
				const cartQty = this.getCartSubscriptionQty()
				const lineCount = this.cartSubscriptionLineCount ?? 0
				const currentQty = this.getCurrentQuantity()
				const countedQty = this.getEffectiveQuantity()
				const unlockedTier = this.getUnlockedTier(countedQty)
				const nextTier = this.getNextTier(countedQty)

				console.group(`[Subscription QtyBreaks] ${context}`)
				console.log('matched quantity in cart (sum of line item quantities):', cartQty)
				console.log('matched line items in cart (number of cart rows, not units):', lineCount)
				console.log('current quantity (PDP input):', currentQty)
				console.log('counted quantity (cart + PDP):', countedQty)

				if (passedQuantity !== null && passedQuantity !== countedQty) {
					console.warn('passed quantity does not match counted quantity:', passedQuantity, countedQty)
				}

				console.log('tiers:', this.tiers)
				console.log('unlocked tier:', unlockedTier)
				console.log('next tier:', nextTier)

				if (nextTier) {
					console.log(
						'unlock message:',
						`Add ${nextTier.quantity - countedQty} more to unlock ${nextTier.savings}% off`
					)
				}

				console.log('Liquid JSON cartSubscriptionQty:', this.subscriptionData.cartSubscriptionQty)
				console.log('Liquid JSON cartSubscriptionLineCount:', this.subscriptionData.cartSubscriptionLineCount)
				console.log('note: counted quantity = cart subscription qty + PDP quantity selector')
				console.groupEnd()
			}

			getQtyInputEl() {
				return document.querySelector(`l-product-quantity-input[data-section-id="${this.sectionId}"] [data-qty-input]`)
			}

			setQuantity(quantity) {
				const qtyInput = this.getQtyInputEl()
				if (!qtyInput) return

				const previousValue = qtyInput.value
				const nextValue = parseInt(quantity, 10) || 1
				qtyInput.value = nextValue
				qtyInput.dispatchEvent(new Event('change', { bubbles: true }))

				const qtyComponent = qtyInput.closest('l-product-quantity-input')
				if (typeof qtyComponent?.onQuantityChanged === 'function') {
					qtyComponent.onQuantityChanged(previousValue, nextValue)
				}
			}

			getUnlockedTier(quantity) {
				return [...this.tiers].reverse().find((tier) => quantity >= tier.quantity) || null
			}

			getNextTier(quantity) {
				return this.tiers.find((tier) => quantity < tier.quantity) || null
			}

			getMilestonePosition(index) {
				const count = this.tiers.length
				const startPercent = 30
				const spanPercent = 70

				if (count <= 1) return startPercent

				return startPercent + (index / (count - 1)) * spanPercent
			}

			positionMilestones() {
				this.qtyBreakMilestoneEls.forEach((milestoneEl, index) => {
					milestoneEl.style.left = `${this.getMilestonePosition(index)}%`
				})
			}

			getTierFillPercent(quantity) {
				if (!this.tiers.length) return 0
				if (quantity <= 0) return 0

				const maxTier = this.tiers[this.tiers.length - 1]
				if (quantity >= maxTier.quantity) return 100

				const firstTier = this.tiers[0]
				if (quantity < firstTier.quantity) {
					const firstPos = this.getMilestonePosition(0)
					return (quantity / firstTier.quantity) * firstPos
				}

				for (let index = 1; index < this.tiers.length; index++) {
					const tier = this.tiers[index]
					const prevTier = this.tiers[index - 1]

					if (quantity < tier.quantity) {
						const prevPos = this.getMilestonePosition(index - 1)
						const currPos = this.getMilestonePosition(index)
						const segmentProgress = (quantity - prevTier.quantity) / (tier.quantity - prevTier.quantity)

						return prevPos + segmentProgress * (currPos - prevPos)
					}
				}

				return 100
			}

			formatUnlockMessage(template, count, savings) {
				if (!template) return ''

				return template
					.replace(/\[count\]/g, `<strong>${count}</strong>`)
					.replace(/\[savings\]/g, `<strong>${savings}%</strong>`)
			}

			formatMaxTierMessage(template, savings) {
				if (!template) return ''

				return template.replace(/\[savings\]/g, String(savings))
			}

			isSubscriptionSelected() {
				const checkedOption = this.querySelector('[data-purchase-option][name="selling_plan"]:checked')
				return Boolean(checkedOption?.value)
			}

			setPanelExpanded(element, expanded) {
				if (!element) return

				if (expanded) {
					l_setElementExpanded(element)
				} else {
					l_setElementCollapsed(element)
				}
			}

			animatePanel(element, expanded) {
				if (!element) return

				const isExpanded =
					element.classList.contains('is-expanded') &&
					!element.hasAttribute('hidden') &&
					!element.classList.contains('l-hidden')

				if (expanded && isExpanded) return
				if (!expanded && element.hasAttribute('hidden')) return

				if (expanded) {
					l_animateElementExpand(element)
				} else {
					l_animateElementCollapse(element)
				}
			}

			syncPurchasePanels({ animate = false } = {}) {
				const isSubscription = this.isSubscriptionSelected()
				const shouldShowMaxTier =
					!isSubscription &&
					Boolean(this.subscriptionData.maxTierBoxMessage) &&
					this.subscriptionData.maxTierBoxAvailable !== false

				if (shouldShowMaxTier) {
					this.updateMaxTierBoxContent()
				}

				if (animate) {
					this.animatePanel(this.subscriptionBodyEl, isSubscription)
					this.animatePanel(this.maxTierPanelEl, shouldShowMaxTier)
					return
				}

				this.setPanelExpanded(this.subscriptionBodyEl, isSubscription)
				this.setPanelExpanded(this.maxTierPanelEl, shouldShowMaxTier)
			}

			updateQtyBreaks(quantity, { animatePanels = false } = {}) {
				if (animatePanels) {
					this.syncPurchasePanels({ animate: true })
				}

				if (!this.subscriptionData.showQuantityBreaks || !this.qtyBreaksEl) return

				this.logQtyBreaksState('updateQtyBreaks', quantity)

				const fillPercent = this.getTierFillPercent(quantity)
				const unlockedTier = this.getUnlockedTier(quantity)
				const nextTier = this.getNextTier(quantity)
				const maxTier = this.tiers[this.tiers.length - 1]
				const isMaxTierUnlocked = maxTier && quantity >= maxTier.quantity

				if (this.qtyBreaksFillEl) {
					this.qtyBreaksFillEl.style.width = `${fillPercent}%`
				}

				this.qtyBreakMilestoneEls.forEach((milestoneEl) => {
					const tierQty = parseInt(milestoneEl.dataset.quantity, 10)
					milestoneEl.classList.toggle('is-unlocked', quantity >= tierQty)
				})

				if (!this.qtyBreaksMessageEl) return

				if (isMaxTierUnlocked && unlockedTier) {
					this.qtyBreaksMessageEl.classList.add('is-max-tier')
					this.qtyBreaksMessageEl.innerHTML = this.formatMaxTierMessage(
						this.subscriptionData.maxTierMessage,
						unlockedTier.savings
					)
					return
				}

				this.qtyBreaksMessageEl.classList.remove('is-max-tier')

				if (nextTier) {
					const remaining = nextTier.quantity - quantity
					this.qtyBreaksMessageEl.innerHTML = this.formatUnlockMessage(
						this.subscriptionData.unlockMessage,
						remaining,
						nextTier.savings
					)
					return
				}

				this.qtyBreaksMessageEl.innerHTML = ''
			}

			updatePrices(quantity) {
				const { variantPrice, sellingPlanPrice, sellingPlanCompareAtPrice, priceAdjustmentType, saveLabel } =
					this.subscriptionData

				const qty = parseInt(quantity, 10) || 1
				const baseVariantPrice = parseInt(variantPrice, 10) || 0
				const baseSellingPlanPrice = parseInt(sellingPlanPrice, 10) || 0
				const baseSellingPlanCompareAtPrice = parseInt(sellingPlanCompareAtPrice, 10) || 0

				// Qty-break tier is based on cart subscription qty + PDP qty (e.g. 3 + 3 = 6)
				const unlockedTier = this.tiers?.length ? this.getUnlockedTier(this.getEffectiveQuantity()) : null
				const savings = parseFloat(unlockedTier?.savings) || 0
				const discountedSellingPlanPrice =
					savings > 0 ? Math.round((baseSellingPlanPrice * (100 - savings)) / 100) : baseSellingPlanPrice
				const compareAtUnit = Math.max(baseSellingPlanCompareAtPrice, baseSellingPlanPrice)
				const showCompareAt = compareAtUnit > discountedSellingPlanPrice

				if (this.variantPriceEl) this.variantPriceEl.innerHTML = l_formatMoney(baseVariantPrice * qty)
				if (this.sellingPriceEl) this.sellingPriceEl.innerHTML = l_formatMoney(discountedSellingPlanPrice * qty)
				if (this.sellingComparePriceEl) {
					this.sellingComparePriceEl.innerHTML = showCompareAt ? l_formatMoney(compareAtUnit * qty) : ''
				}

				if (priceAdjustmentType == 'price' && this.saveLabelEl) {
					const reducedPrice = l_formatMoney((compareAtUnit - discountedSellingPlanPrice) * qty)
					this.saveLabelEl.innerHTML = saveLabel.replace('[save_amount]', reducedPrice)
				}
			}

			dispatchQuantityChanged(quantity, savingsPercentage) {
				this.dispatchEvent(
					new CustomEvent('l:quantity:changed', {
						bubbles: true,
						detail: {
							quantity,
							savingsPercentage,
							productId: this.productId,
							sectionId: this.sectionId,
						},
					})
				)
			}

			handleOnQuantityChange(event) {
				const { sectionId, productId, quantity } = event.detail

				if (productId !== this.productId || sectionId !== this.sectionId) return
				if (event.target === this) return

				const inputQuantity = parseInt(quantity, 10) || 1
				this.subscriptionData = {
					...this.subscriptionData,
					quantity: inputQuantity,
				}

				const effectiveQuantity = this.getEffectiveQuantity()
				const unlockedTier = this.getUnlockedTier(effectiveQuantity)

				this.updatePrices(inputQuantity)

				if (this.subscriptionData.showQuantityBreaks) {
					this.updateQtyBreaks(effectiveQuantity)
				}

				this.dispatchQuantityChanged(inputQuantity, unlockedTier?.savings || null)
			}

			handleOnVariantChange(event) {
				const { sectionId, html, productId, variant, preservePurchaseOption, preservedSellingPlanId } = event.detail

				if (productId !== this.productId || sectionId !== this.sectionId) return

				this.classList.toggle('l-hidden', !variant)
				if (!variant) return

				const newSubscriptionsEl = html.getElementById(`Subscriptions-${this.sectionId}`)

				if (newSubscriptionsEl) {
					this.innerHTML = newSubscriptionsEl.innerHTML
					this.init()

					if (preservePurchaseOption) {
						this.restorePurchaseOption(preservedSellingPlanId)
					}
				} else {
					this.classList.add('l-hidden')
				}
			}

			restorePurchaseOption(sellingPlanId) {
				const selector = sellingPlanId
					? `[name="selling_plan"][value="${CSS.escape(String(sellingPlanId))}"]`
					: '[name="selling_plan"][value=""]'
				const radio = this.querySelector(selector)

				if (radio && !radio.checked) {
					radio.checked = true
					this.selectedSellingPlanId = sellingPlanId || null
					this.syncSellingPlanSelect()
				}

				this.updateQtyBreaks(this.getEffectiveQuantity(), { animatePanels: true })
			}

			syncSellingPlanSelect() {
				if (!this.sellingPlanSelectEl) return

				const checkedRadio = this.querySelector('[data-purchase-option][name="selling_plan"]:checked:not([value=""])')
				if (checkedRadio?.value) {
					this.sellingPlanSelectEl.value = checkedRadio.value
				}
			}

			handleSellingPlanSelect(event) {
				const sellingPlanId = event.target.value
				const matchingRadio = this.querySelector(
					`[data-purchase-option][name="selling_plan"][value="${CSS.escape(sellingPlanId)}"]`
				)

				if (matchingRadio) {
					matchingRadio.checked = true
					matchingRadio.dispatchEvent(new Event('change', { bubbles: true }))
				}
			}

			handleMilestoneClick(event) {
				const milestoneEl = event.target.closest('[data-qty-breaks-milestone]')
				if (!milestoneEl || !this.contains(milestoneEl)) return

				const tierQuantity = parseInt(milestoneEl.dataset.quantity, 10)
				if (!tierQuantity) return

				const inputQuantity = Math.max(1, tierQuantity - this.getCartSubscriptionQty())
				this.setQuantity(inputQuantity)
			}

			handleOnChange(event) {
				const target = event.target
				if (target?.getAttribute('name') !== 'selling_plan') return

				this.selectedSellingPlanId = target.value
				this.syncSellingPlanSelect()
				this.dispatchSellingPlanIdChanged()

				if (!this.isSubscriptionSelected()) {
					this.hideQtyBreaks()
				} else if (this.dataset.qtyBreaksEligible === 'true' && !this.subscriptionData.showQuantityBreaks) {
					this.initQtyBreakTiers(this.getEffectiveQuantity())
				}

				this.syncPurchasePanels({ animate: true })

				if (this.subscriptionData.showQuantityBreaks) {
					this.updateQtyBreaks(this.getEffectiveQuantity())
				}
			}

			dispatchSellingPlanIdChanged() {
				this.dispatchEvent(
					new CustomEvent('l:sellingPlan:changed', {
						bubbles: true,
						detail: {
							sellingPlanId: this.selectedSellingPlanId,
							productId: this.productId,
							sectionId: this.sectionId,
							selectedVariantId: this.selectedVariantId,
							target: this,
						},
					})
				)
			}

			disableAppInputsIfNeeded() {
				document.addEventListener('sealsubs:subscription_widget_created', function () {
					const productEl = document.getElementById('product')
					if (!productEl) return
					const sealEls = productEl.querySelectorAll('.sealsubs-target-element')
					sealEls.forEach((sealEl) => {
						sealEl.classList.add('l-hidden')
						const sellingPlanInputs = sealEl.querySelectorAll('[name="selling_plan"]')
						sellingPlanInputs.forEach((input) => {
							input.setAttribute('disabled', 'disabled')
						})
					})
				})
			}
		}

		customElements.define('l-subscriptions-selling-plans-v2', LTASubscriptionSellingPlansV2)
	}

	/**********************************
	 * [PRODUCT BLOCK]: QUANTITY BREAKS
	 ***********************************/

	if (!customElements.get('l-product-quantity-breaks')) {
		customElements.define(
			'l-product-quantity-breaks',
			class LTAProductQuantityBreaks extends HTMLElement {
				constructor() {
					super()
					this._onVariantChange = this._onVariantChange.bind(this)
					this._onInputChange = this._onInputChange.bind(this)
				}

				connectedCallback() {
					this.productId = this.dataset.productId
					this.sectionId = this.dataset.sectionId

					document.addEventListener('l:variant:changed', this._onVariantChange)
					this.addEventListener('change', this._onInputChange)
				}

				disconnectedCallback() {
					document.removeEventListener('l:variant:changed', this._onVariantChange)
					this.removeEventListener('change', this._onInputChange)
				}

				_onVariantChange(event) {
					const { sectionId, html, productId, variant } = event.detail || {}

					if (productId !== this.productId || sectionId !== this.sectionId) return

					this.classList.toggle('l-hidden', !variant)

					const newQuantityBreaksEl = html.getElementById(`QtyBreaks-${this.sectionId}`)

					if (newQuantityBreaksEl) {
						const previousValue = this.querySelector('input[type="radio"][name="quantity"]:checked')?.value ?? ''

						this.innerHTML = newQuantityBreaksEl.innerHTML

						if (previousValue) {
							const input = this.querySelector(
								`input[type="radio"][name="quantity"][value="${CSS.escape(previousValue)}"]`
							)

							if (input) {
								input.checked = true
								// Optionally: input.dispatchEvent(new Event('change', { bubbles: true }));
							}
						}
					}
				}

				_onInputChange(event) {
					const input = event.target
					const isValidInput = input && input.tagName === 'INPUT' && input.type === 'radio' && input.name === 'quantity'

					if (isValidInput) {
						const quantity = input.value
						const price = input.dataset.price
						const priceTotal = input.dataset.priceTotal
						const savingsPercentage = input.dataset.savingsPercentage

						this.dispatchEvent(
							new CustomEvent('l:quantity:changed', {
								bubbles: true,
								detail: {
									quantity,
									savingsPercentage,
									productId: this.productId,
									sectionId: this.sectionId,
								},
							})
						)
					}
				}
			}
		)
	}

	/**********************************
	 * [PRODUCT BLOCK]: COMBINED PRODUCTS
	 ***********************************/

	if (!customElements.get('l-combined-products')) {
		class LTACombinedProducts extends HTMLElement {
			constructor() {
				super()

				this._onProductLinkClick = this._onProductLinkClick.bind(this)
			}

			connectedCallback() {
				this._addEventListeners()
			}

			disconnectedCallback() {
				this._removeEventListeners()
			}

			_addEventListeners() {
				this.productLinkEls = this.querySelectorAll('[data-product-link]')
				this.productLinkEls.forEach((el) => {
					el.addEventListener('click', this._onProductLinkClick)
				})
			}

			_removeEventListeners() {
				if (this.productLinkEls) {
					this.productLinkEls.forEach((el) => {
						el.removeEventListener('click', this._onProductLinkClick)
					})
				}
			}

			_onProductLinkClick(event) {
				// PIQ preorder fix (scoped): switchers flagged data-full-navigation
				// (Globo pre-order products) skip the in-place AJAX swap and let the
				// swatch <a href> navigate normally — a full page load re-runs Globo
				// so the Pre-Order button renders correctly (Globo exposes no re-init
				// API). All other switchers keep the instant in-place swap.
				if (this.dataset.fullNavigation === 'true') return

				event.preventDefault()
				const productLinkEl = event.currentTarget
				const productLink = productLinkEl.dataset.productLink
				if (!productLink) return

				this.dispatchEvent(
					new CustomEvent('l:combined-product:selected', {
						detail: { productLink, targetId: this.id },
						bubbles: true,
					})
				)
			}
		}

		customElements.define('l-combined-products', LTACombinedProducts)
	}
})

